import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class HotelBenchmarkInsightTermsAndConditionsController extends Controller {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked hasAcceptedTermsAndConditions = false;

  @action
  async onClickContinue(): Promise<void> {
    this.usageAnalytics.track('Compset Consent Agreed', {
      eventTrigger: 'terms-and-conditions',
      product: 'benchmark-insight',
    });

    try {
      const url = formatUrl(
        '/api/v3/benchmarkinsight/terms_and_conditions/accept/',
        {
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
          subscription_id: this.hotelAndMarketService.hotel.id,
        },
        this.ajax.host,
      );

      await this.ajax.request(url, { method: 'POST' });

      // When the user accepts the terms and conditions, BE sets the `benchmark_insight.data` feature flag to true.
      // At the end of this action, we will redirect the user to the Benchmark Insight product.
      // FE first needs to refetch the feature flags, otherwise the user will be redirected away from the Benchmark Insight product again.
      // Staff users can skip the terms and conditions, so we don't need to refetch the feature flags for them.
      if (!this.access.hasBenchmarkInsightDataStaffOnly) {
        await this.access.refetchFeatureFlags();
      }

      this.hasAcceptedTermsAndConditions = true;
    } catch (error) {
      // Don't show error alert or track errors for staff users, as they will be redirected to the Benchmark Insight product.
      // Showing an error alert could cause confusion: staff users could think that the error is coming from the Benchmark Insight product.
      if (this.access.hasBenchmarkInsightDataStaffOnly) {
        return;
      }

      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
    } finally {
      // Accepting the terms and conditions can only be done by admin users of the subscription, not by normal users or staff users.
      // Normal users will not see the button to trigger this request.
      // For staff users, we perform the request for debugging purposes,
      // and they are allowed to skip the terms and conditions even though the backend will reject the request.
      if (this.access.hasBenchmarkInsightDataStaffOnly) {
        this.router.transitionTo('hotel.benchmark-insight.index', {
          queryParams: { viewAsStaffUser: true },
        });
      }

      if (this.hasAcceptedTermsAndConditions) {
        this.router.transitionTo('hotel.benchmark-insight.index');
      }
    }
  }
}
